import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageTitle from '../components/PageTitle';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import GeneralTab from './settings/GeneralTab';
import SnowflakeConnectionTab from './settings/SnowflakeConnectionTab';
import DbtBuildsTab from './settings/DbtBuildsTab';
import WhiteLabelTab from './settings/WhiteLabelTab';
import EllieTab from './settings/EllieTab';
import AnalyticsTab from './settings/AnalyticsTab';
import GitTab from './settings/GitTab';
import McpTab from './settings/McpTab';
import JimiTab from './settings/JimiTab';
import { useUserConfig } from '@/hooks/useUserConfig';
import { useUserRoles } from '@/hooks/useUserRoles';
import AccessDenied from '@/components/AccessDenied';

const VALID_TABS = ['general', 'snowflake', 'dbt-builds', 'white-label', 'ellie', 'analytics', 'git', 'mcp'];
// Only Pliable staff can see or switch Jimi; the backend refuses everyone else too.
const STAFF_TABS = ['jimi'];

export default function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: userConfig } = useUserConfig();
  const isStaff = !!userConfig?.is_internal_staff;
  // Every tab but MCP is Admin-only on the backend; without this gate a non-admin
  // sees forms whose requests 403 into endless spinners.
  const { isAdmin } = useUserRoles();
  const adminOnly = (tab: ReactNode) => (isAdmin ? tab : <AccessDenied />);
  const tabParam = searchParams.get('tab') ?? '';
  const activeTab = VALID_TABS.includes(tabParam) || (isStaff && STAFF_TABS.includes(tabParam)) ? tabParam : 'general';

  const setActiveTab = (tab: string) => {
    setSearchParams(tab === 'general' ? {} : { tab }, { replace: true });
  };

  return (
    <div className="p-8 h-full overflow-auto">
      <PageTitle title="Settings" className="mb-6" />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="snowflake">Snowflake Connection</TabsTrigger>
          <TabsTrigger value="dbt-builds">DBT Builds</TabsTrigger>
          <TabsTrigger value="white-label">White Label</TabsTrigger>
          <TabsTrigger value="ellie">Ellie</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="git">Git</TabsTrigger>
          <TabsTrigger value="mcp">MCP</TabsTrigger>
          {isStaff && <TabsTrigger value="jimi">Jimi</TabsTrigger>}
        </TabsList>

        <TabsContent value="general" className="mt-6">
          {adminOnly(<GeneralTab />)}
        </TabsContent>

        <TabsContent value="snowflake" className="mt-6">
          {adminOnly(<SnowflakeConnectionTab />)}
        </TabsContent>

        <TabsContent value="dbt-builds" className="mt-6">
          {adminOnly(<DbtBuildsTab />)}
        </TabsContent>

        <TabsContent value="white-label" className="mt-6">
          {adminOnly(<WhiteLabelTab />)}
        </TabsContent>

        <TabsContent value="ellie" className="mt-6">
          {adminOnly(<EllieTab />)}
        </TabsContent>

        <TabsContent value="analytics" className="mt-6">
          {adminOnly(<AnalyticsTab />)}
        </TabsContent>

        <TabsContent value="mcp" className="mt-6">
          <McpTab />
        </TabsContent>

        <TabsContent value="git" className="mt-6">
          {adminOnly(<GitTab />)}
        </TabsContent>

        {isStaff && (
          <TabsContent value="jimi" className="mt-6">
            {adminOnly(<JimiTab />)}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
